// This Modal is being used to assign lead and deputy lead to event, area, and shift

import React, { useState, useEffect, useRef } from 'react'
import { Volunteer, LeadAssignModalPropsMeta } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { LoadingSpinner } from './LoadingSpinner'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '../icons'
import { formatDateOnly } from '../utils/dates'

const ITEMS_PER_PAGE = 10

type Props = {
    meta: LeadAssignModalPropsMeta
    title: string
    isOpen: boolean
    onSubmit: (volunteer: Volunteer) => Promise<void>
    onClose: () => void
    currentLead?: Volunteer
}

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

export const LeadAssignModal = (props: Props) => {
    const { meta, title, isOpen, onClose, onSubmit, currentLead } = props
    const [volunteers, setVolunteers] = useState<Volunteer[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [loadingPage, setLoadingPage] = useState<number>(1)
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })

    // Search state
    const [nameSearch, setNameSearch] = useState('')
    const [emailSearch, setEmailSearch] = useState('')
    const [phoneSearch, setPhoneSearch] = useState('')
    const [idSearch, setIdSearch] = useState('')

    // Applied filters (what's actually sent to the server)
    const [appliedFilters, setAppliedFilters] = useState({
        name: '',
        email: '',
        phone: '',
        id: ''
    })

    // Duplications modal state
    const [showDuplicationsModal, setShowDuplicationsModal] = useState(false)
    const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null)
    const [showExcludedSections, setShowExcludedSections] = useState(false)

    const hasFetched = useRef(false)

    useEffect(() => {
        if (isOpen && !hasFetched.current) {
            hasFetched.current = true
            fetchVolunteers(1, appliedFilters)
        }
    }, [isOpen])

    const buildQueryString = (page: number, filters: typeof appliedFilters) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())

        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        if (filters.phone) params.append('phone', filters.phone)
        if (filters.id) params.append('id', filters.id)

        return params.toString()
    }

    const fetchVolunteers = async (page: number, filters: typeof appliedFilters) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, filters)
            const response = await fetch(
                `${API_BASE_URL}/volunteer?${queryString}`,
                { credentials: 'include' }
            )
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()

            // Extract data and pagination info
            const volunteersData = result.data || []
            const normalized = volunteersData.map((v: any) => ({
                ...v,
                eligible_for_duty: v.eligible_for_duty === 1,
                ismaili: v.ismaili === 1,
                registered_volunteer: v.registered_volunteer === 1,
                multi_faith_family: v.multi_faith_family === 1,
                volunteer_for_visit: v.volunteer_for_visit !== null && v.volunteer_for_visit !== undefined ? Number(v.volunteer_for_visit) : v.volunteer_for_visit,
                allocations: v.allocations || [],
                coreTeams: v.core_teams || v.coreTeams || [], // Handle both snake_case and camelCase
                eventLeads: v.event_leads || v.eventLeads || [],
                areasAsLead: v.areas_as_lead || v.areasAsLead || [],
                areasAsDeputyLead: v.areas_as_deputy_lead || v.areasAsDeputyLead || [],
                shiftsAsLead: v.shifts_as_lead || v.shiftsAsLead || [],
                shiftsAsDeputyLead: v.shifts_as_deputy_lead || v.shiftsAsDeputyLead || []
            }))

            // Filter out current lead if present
            const filteredVolunteers = currentLead
                ? normalized.filter((v: Volunteer) => v.csid !== currentLead.csid)
                : normalized

            setVolunteers(filteredVolunteers)
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        const filters = {
            name: nameSearch.trim(),
            email: emailSearch.trim(),
            phone: phoneSearch.trim(),
            id: idSearch.trim()
        }
        setAppliedFilters(filters)
        fetchVolunteers(1, filters)
    }

    const handleClear = () => {
        setNameSearch('')
        setEmailSearch('')
        setPhoneSearch('')
        setIdSearch('')
        const emptyFilters = { name: '', email: '', phone: '', id: '' }
        setAppliedFilters(emptyFilters)
        fetchVolunteers(1, emptyFilters)
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page, appliedFilters)
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setError(null)
        }
    }, [isOpen])

    const handleSubmit = async (volunteer: Volunteer) => {
        setLoading(true)
        try {
            await onSubmit(volunteer)
            handleClose()
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        }
        finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setError(null)
        onClose()
    }

    return (
        <>
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            maxWidth="5xl"
            footer={
                <div className="flex justify-end">
                    <Button variant="outline" onClick={handleClose}>
                        {meta.cancelButton}
                    </Button>
                </div>
            }
        >
            <div className="px-6 py-4 space-y-4">
                {/* Search Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <input
                        type="text"
                        placeholder={meta.nameSearchPlaceholder}
                        value={nameSearch}
                        onChange={(e) => setNameSearch(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        placeholder={meta.emailSearchPlaceholder}
                        value={emailSearch}
                        onChange={(e) => setEmailSearch(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        placeholder={meta.phoneSearchPlaceholder}
                        value={phoneSearch}
                        onChange={(e) => setPhoneSearch(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        placeholder={meta.idSearchPlaceholder}
                        value={idSearch}
                        onChange={(e) => setIdSearch(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                </div>

                {/* Search Buttons */}
                <div className="flex gap-2">
                    <Button onClick={handleSearch} size="sm">
                        <SearchIcon className="w-4 h-4" />
                        {meta.searchButton}
                    </Button>
                    <Button onClick={handleClear} variant="outline" size="sm">
                        {meta.clearButton}
                    </Button>
                </div>

                {error && (
                    <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {meta.showingLabel}: {pagination.total} {meta.volunteersLabel}
                </div>

                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    ) : volunteers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {(nameSearch || emailSearch || phoneSearch || idSearch) ? meta.noResultsMessage : meta.noVolunteersMessage}
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                                <tr>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.idColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.nameColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.phoneColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.emailColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.jamatKhanaColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.duplicationsColumn}
                                    </th>
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.actionColumn}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {volunteers.map((volunteer: any) => {
                                    // Use duplicate_check_allocations_count which excludes allocations from events with include_in_duplicate_checks = false
                                    const allocationsCount = volunteer.duplicate_check_allocations_count ?? 0
                                    const coreTeamsCount = volunteer.core_teams_count ?? 0
                                    // Use filtered lead counts that exclude positions from events with include_in_duplicate_checks = false
                                    const leadPositionsCount = (
                                        (volunteer.duplicate_check_event_leads_count ?? 0) +
                                        (volunteer.duplicate_check_areas_as_lead_count ?? 0) +
                                        (volunteer.duplicate_check_areas_as_deputy_lead_count ?? 0) +
                                        (volunteer.duplicate_check_shifts_as_lead_count ?? 0) +
                                        (volunteer.duplicate_check_shifts_as_deputy_lead_count ?? 0)
                                    )
                                    const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount

                                    return (
                                        <tr
                                            key={volunteer.csid}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.csid}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.first_name} {volunteer.last_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.phone || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.jamat_khana || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {totalCount === 0 ? (
                                                    <span className="text-gray-500 dark:text-gray-400">None</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedVolunteer(volunteer)
                                                            setShowDuplicationsModal(true)
                                                        }}
                                                    >
                                                        {meta.viewDuplicationsButton(totalCount)}
                                                    </Button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    onClick={() => handleSubmit(volunteer)}
                                                    disabled={loading}
                                                >
                                                    {meta.selectButton}
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination Controls */}
                {pagination.last_page > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.current_page <= 1}
                                size="sm"
                            >
                                <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                            </Button>

                            <Button
                                type="button"
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page <= 1}
                                size="sm"
                            >
                                <ChevronLeftIcon /> Previous
                            </Button>

                            {(() => {
                                const pageNumbers = []
                                const maxPagesToShow = 5
                                let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2))
                                let endPage = Math.min(pagination.last_page, startPage + maxPagesToShow - 1)

                                if (endPage - startPage < maxPagesToShow - 1) {
                                    startPage = Math.max(1, endPage - maxPagesToShow + 1)
                                }

                                if (startPage > 1) {
                                    pageNumbers.push(
                                        <Button
                                            key={1}
                                            type="button"
                                            onClick={() => handlePageChange(1)}
                                            size="sm"
                                            variant="icon"
                                        >
                                            1
                                        </Button>
                                    )
                                    if (startPage > 2) {
                                        pageNumbers.push(
                                            <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                                        )
                                    }
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                    pageNumbers.push(
                                        <Button
                                            key={i}
                                            type="button"
                                            onClick={() => handlePageChange(i)}
                                            size="sm"
                                            variant={i === loadingPage ? "solid" : "icon"}
                                            color={i === loadingPage ? "primary" : "neutral"}
                                        >
                                            {i}
                                        </Button>
                                    )
                                }

                                if (endPage < pagination.last_page) {
                                    if (endPage < pagination.last_page - 1) {
                                        pageNumbers.push(
                                            <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                                        )
                                    }
                                    pageNumbers.push(
                                        <Button
                                            key={pagination.last_page}
                                            type="button"
                                            onClick={() => handlePageChange(pagination.last_page)}
                                            size="sm"
                                            variant="icon"
                                        >
                                            {pagination.last_page}
                                        </Button>
                                    )
                                }

                                return pageNumbers
                            })()}

                            <Button
                                type="button"
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page >= pagination.last_page}
                                size="sm"
                            >
                                Next <ChevronRightIcon />
                            </Button>

                            <Button
                                type="button"
                                onClick={() => handlePageChange(pagination.last_page)}
                                disabled={pagination.current_page >= pagination.last_page}
                                size="sm"
                            >
                                Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                            </Button>
                        </div>

                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Page {pagination.current_page} of {pagination.last_page}
                        </span>
                    </div>
)}
            </div>
        </Modal>

        {/* Duplications Modal */}
        {showDuplicationsModal && selectedVolunteer && (
            <Modal
                isOpen={showDuplicationsModal}
                onClose={() => {
                    setShowDuplicationsModal(false)
                    setSelectedVolunteer(null)
                }}
                title={meta.duplicationsModalTitle(selectedVolunteer.first_name, selectedVolunteer.last_name)}
                maxWidth="5xl"
                footer={
                    <div className="flex justify-end">
                        <Button
                            onClick={() => {
                                setShowDuplicationsModal(false)
                                setSelectedVolunteer(null)
                            }}
                            variant="outline"
                        >
                            {meta.closeModalButton}
                        </Button>
                    </div>
                }
            >
                <div className="px-6 py-4">
                    {/* Shift Allocations - Split into counted and excluded */}
                    {selectedVolunteer.allocations && selectedVolunteer.allocations.length > 0 && (() => {
                        const countedAllocations = selectedVolunteer.allocations.filter(
                            (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks !== false
                        )
                        const excludedAllocations = selectedVolunteer.allocations.filter(
                            (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks === false
                        )

                        const renderAllocationTable = (allocations: any[], isExcluded: boolean = false) => (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className={isExcluded ? "bg-gray-100 dark:bg-gray-700" : "bg-gray-50 dark:bg-gray-800"}>
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.dateTimeColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`${isExcluded ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"} divide-y divide-gray-200 dark:divide-gray-700`}>
                                        {allocations.map((allocation: any, idx: number) => {
                                            const shift = allocation.shift
                                            const eventDate = shift?.area?.event?.start_date || shift?.area?.event?.date
                                            const startTime = shift?.start_time || 'N/A'
                                            const endTime = shift?.end_time || 'N/A'

                                            // Get status from allocation_times
                                            let statusText = meta.pendingStatus
                                            let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                            if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                const allocationTime = allocation.allocation_times[0]
                                                if (allocationTime.status === null) {
                                                    statusText = meta.pendingStatus
                                                    statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                } else if (allocationTime.status === true || allocationTime.status === 1) {
                                                    statusText = meta.acceptedStatus
                                                    statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                    statusText = meta.declinedStatus
                                                    statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                                                }
                                            }

                                            return (
                                                <tr key={idx} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.event?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.title || shift?.duty?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {formatDateOnly(eventDate)} {startTime} - {endTime}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )

                        return (
                            <>
                                {/* Counted Allocations */}
                                {countedAllocations.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                            {meta.shiftAllocationsTitle(countedAllocations.length)}
                                        </h3>
                                        {renderAllocationTable(countedAllocations)}
                                    </div>
                                )}

                                {/* Store excluded allocations for later rendering */}
                                {excludedAllocations.length > 0 && (
                                    <input type="hidden" data-excluded-allocations={JSON.stringify(excludedAllocations)} />
                                )}
                            </>
                        )
                    })()}

                    {/* Core Team Assignments */}
                    {selectedVolunteer.coreTeams && selectedVolunteer.coreTeams.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                {meta.coreTeamAssignmentsTitle(selectedVolunteer.coreTeams.length)}
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.streamsColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.roleColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {selectedVolunteer.coreTeams.map((coreTeam: any, idx: number) => {
                                            const statusText = coreTeam.approved ? meta.approvedStatus : meta.submittedStatus
                                            const statusClass = coreTeam.approved
                                                ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                            return (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {Array.isArray(coreTeam.streams) ? coreTeam.streams.join(', ') : coreTeam.streams || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {coreTeam.role || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Lead/Deputy Lead Positions - Split into counted and excluded */}
                    {(() => {
                        // Filter event leads
                        const countedEventLeads = selectedVolunteer.eventLeads?.filter(
                            (event: any) => event.include_in_duplicate_checks !== false
                        ) || []
                        const excludedEventLeads = selectedVolunteer.eventLeads?.filter(
                            (event: any) => event.include_in_duplicate_checks === false
                        ) || []

                        // Filter areas as lead
                        const countedAreasAsLead = selectedVolunteer.areasAsLead?.filter(
                            (area: any) => area.event?.include_in_duplicate_checks !== false
                        ) || []
                        const excludedAreasAsLead = selectedVolunteer.areasAsLead?.filter(
                            (area: any) => area.event?.include_in_duplicate_checks === false
                        ) || []

                        // Filter areas as deputy lead
                        const countedAreasAsDeputyLead = selectedVolunteer.areasAsDeputyLead?.filter(
                            (area: any) => area.event?.include_in_duplicate_checks !== false
                        ) || []
                        const excludedAreasAsDeputyLead = selectedVolunteer.areasAsDeputyLead?.filter(
                            (area: any) => area.event?.include_in_duplicate_checks === false
                        ) || []

                        // Filter shifts as lead
                        const countedShiftsAsLead = selectedVolunteer.shiftsAsLead?.filter(
                            (shift: any) => shift.area?.event?.include_in_duplicate_checks !== false
                        ) || []
                        const excludedShiftsAsLead = selectedVolunteer.shiftsAsLead?.filter(
                            (shift: any) => shift.area?.event?.include_in_duplicate_checks === false
                        ) || []

                        // Filter shifts as deputy lead
                        const countedShiftsAsDeputyLead = selectedVolunteer.shiftsAsDeputyLead?.filter(
                            (shift: any) => shift.area?.event?.include_in_duplicate_checks !== false
                        ) || []
                        const excludedShiftsAsDeputyLead = selectedVolunteer.shiftsAsDeputyLead?.filter(
                            (shift: any) => shift.area?.event?.include_in_duplicate_checks === false
                        ) || []

                        const countedTotal = countedEventLeads.length + countedAreasAsLead.length + countedAreasAsDeputyLead.length + countedShiftsAsLead.length + countedShiftsAsDeputyLead.length
                        const excludedTotal = excludedEventLeads.length + excludedAreasAsLead.length + excludedAreasAsDeputyLead.length + excludedShiftsAsLead.length + excludedShiftsAsDeputyLead.length

                        // Get excluded allocations for rendering at the end
                        const excludedAllocations = selectedVolunteer.allocations?.filter(
                            (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks === false
                        ) || []

                        const renderLeadPositionsTable = (
                            eventLeads: any[],
                            areasAsLead: any[],
                            areasAsDeputyLead: any[],
                            shiftsAsLead: any[],
                            shiftsAsDeputyLead: any[],
                            isExcluded: boolean = false
                        ) => (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className={isExcluded ? "bg-gray-100 dark:bg-gray-700" : "bg-gray-50 dark:bg-gray-800"}>
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.positionTypeColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.roleColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`${isExcluded ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"} divide-y divide-gray-200 dark:divide-gray-700`}>
                                        {eventLeads.map((event: any, idx: number) => (
                                            <tr key={`event-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Event</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.pivot?.role || 'Lead'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {areasAsLead.map((area: any, idx: number) => (
                                            <tr key={`area-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {areasAsDeputyLead.map((area: any, idx: number) => (
                                            <tr key={`area-deputy-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {shiftsAsLead.map((shift: any, idx: number) => (
                                            <tr key={`shift-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.title || shift.duty?.name || 'N/A'}</td>
                                            </tr>
                                        ))}
                                        {shiftsAsDeputyLead.map((shift: any, idx: number) => (
                                            <tr key={`shift-deputy-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.title || shift.duty?.name || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )

                        // Helper to render excluded allocations table
                        const renderExcludedAllocationsTable = () => (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-100 dark:bg-gray-700">
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.dateTimeColumn}</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-gray-50 dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                                        {excludedAllocations.map((allocation: any, idx: number) => {
                                            const shift = allocation.shift
                                            const eventDate = shift?.area?.event?.start_date || shift?.area?.event?.date
                                            const startTime = shift?.start_time || 'N/A'
                                            const endTime = shift?.end_time || 'N/A'

                                            let statusText = meta.pendingStatus
                                            let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                            if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                const allocationTime = allocation.allocation_times[0]
                                                if (allocationTime.status === null) {
                                                    statusText = meta.pendingStatus
                                                    statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                } else if (allocationTime.status === true || allocationTime.status === 1) {
                                                    statusText = meta.acceptedStatus
                                                    statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                    statusText = meta.declinedStatus
                                                    statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                                                }
                                            }

                                            return (
                                                <tr key={idx} className="opacity-75">
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.event?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.title || shift?.duty?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {formatDateOnly(eventDate)} {startTime} - {endTime}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )

                        return (
                            <>
                                {/* Counted Lead Positions */}
                                {countedTotal > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                            {meta.leadPositionsTitle(countedTotal)}
                                        </h3>
                                        {renderLeadPositionsTable(countedEventLeads, countedAreasAsLead, countedAreasAsDeputyLead, countedShiftsAsLead, countedShiftsAsDeputyLead)}
                                    </div>
                                )}

                                {/* Excluded Sections (Collapsible) */}
                                    {(excludedAllocations.length > 0 || excludedTotal > 0) && (
                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setShowExcludedSections(!showExcludedSections)}
                                                className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-3"
                                            >
                                                <ChevronDownIcon className={`w-5 h-5 transition-transform ${showExcludedSections ? 'rotate-0' : '-rotate-90'}`} />
                                                <span className="text-sm font-medium">
                                                    Show Excluded Items ({excludedAllocations.length + excludedTotal})
                                                </span>
                                            </button>

                                            {showExcludedSections && (
                                                <>
                                                    {/* Excluded Allocations */}
                                                    {excludedAllocations.length > 0 && (
                                                        <div className="mb-6">
                                                            <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                                {meta.excludedAllocationsTitle(excludedAllocations.length)}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                                                {meta.excludedAllocationsDescription}
                                                            </p>
                                                            {renderExcludedAllocationsTable()}
                                                        </div>
                                                    )}

                                                    {/* Excluded Lead Positions */}
                                                    {excludedTotal > 0 && (
                                                        <div className="mb-6">
                                                            <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                                {meta.excludedLeadPositionsTitle(excludedTotal)}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                                                {meta.excludedLeadPositionsDescription}
                                                            </p>
                                                            {renderLeadPositionsTable(excludedEventLeads, excludedAreasAsLead, excludedAreasAsDeputyLead, excludedShiftsAsLead, excludedShiftsAsDeputyLead, true)}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                            </>
                        )
                    })()}

                    {/* No duplications message */}
                    {(!selectedVolunteer.allocations || selectedVolunteer.allocations.length === 0) &&
                     (!selectedVolunteer.coreTeams || selectedVolunteer.coreTeams.length === 0) &&
                     (!selectedVolunteer.eventLeads || selectedVolunteer.eventLeads.length === 0) &&
                     (!selectedVolunteer.areasAsLead || selectedVolunteer.areasAsLead.length === 0) &&
                     (!selectedVolunteer.areasAsDeputyLead || selectedVolunteer.areasAsDeputyLead.length === 0) &&
                     (!selectedVolunteer.shiftsAsLead || selectedVolunteer.shiftsAsLead.length === 0) &&
                     (!selectedVolunteer.shiftsAsDeputyLead || selectedVolunteer.shiftsAsDeputyLead.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {meta.noDuplicationsMessage}
                        </p>
                    )}
                </div>
            </Modal>
        )}
        </>
    )
}
